import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ModalService } from '@shared/components/modal-list/modal-list.service';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, Observable, of, switchMap, take } from 'rxjs';
import { failStatus, GenericRequest, StatusRequest, successStatus } from '@core/models/request-status.model';
import { TermsModalComponent } from '@b2b/features/misc/terms-modal/terms-modal.component';
import { environment } from '@environments/environment';
import { TermsCheckModel, TermsModalModel } from '@b2b/features/misc/terms-modal/utilities/terms-modal.model';
import { AppLoad } from '@core/contracts/app-load';
import { AuthService } from '@core/authentication/services/auth.service';
import { B2B_CUSTOMER_ID_KEY, TERMS_ACCEPTED_KEY } from '@shared/tools/constants';
import { AppFeaturesService } from '@core/services/app-features.service';
import { getCommerceStorageContext } from '@shared/tools/storage-keys';

@Injectable({
  providedIn: 'root',
})
export class TermsModalService implements AppLoad {
  constructor(
    private http: HttpClient,
    private modalService: ModalService<TermsModalModel>,
    private translateService: TranslateService,
    private authService: AuthService,
    private appFeaturesService: AppFeaturesService,
  ) {}

  private getCustomerId(): string | null {
    return localStorage.getItem(B2B_CUSTOMER_ID_KEY);
  }

  load(): Observable<void> {
    if (!this.appFeaturesService.isB2BEnabled()) {
      return of(void 0);
    }

    if (getCommerceStorageContext() === 'asm') {
      return of(void 0);
    }

    if (this.authService.isAuthenticated()) {
      const customerId = this.getCustomerId();
      if (!customerId) return of(void 0);

      // Verificar localStorage primeiro para evitar flash da modal
      const localAcceptance = localStorage.getItem(TERMS_ACCEPTED_KEY);

      if (localAcceptance === 'true') {
        // Usuário já aceitou, não precisa verificar no backend
        return of(void 0);
      }

      const url = `${environment.apiService}/terms/check-acceptance?userId=${customerId}`;

      this.http
        .get<TermsCheckModel>(url)
        .pipe(
          switchMap(response => {
            if (!response.accepted) {
              return this.getContent();
            }

            // Salvar no localStorage que o usuário já aceitou
            localStorage.setItem(TERMS_ACCEPTED_KEY, 'true');
            return of(null);
          }),
          take(1),
        )
        .subscribe({
          next: response => {
            if (response) {
              this.modalService.addModal({
                data: response.data,
                disableClose: true,
                hasCloseButton: false,
                id: 'terms-modal',
                title: '',
                width: 1140,
                component: TermsModalComponent,
              });
            }
          },
          error: () => {
            this.translateService.get('generic.requestError').pipe(
              map(
                _message =>
                  ({
                    ...failStatus,
                    message: _message,
                  }) as StatusRequest,
              ),
            );
          },
        });
    }

    return of(void 0);
  }

  checkAcceptance() {
    const customerId = this.getCustomerId();
    if (!customerId) {
      return this.translateService.get('generic.requestError').pipe(
        map(_message => ({
          ...failStatus,
          message: 'User ID not found',
        })),
      );
    }

    const url = `${environment.apiService}/terms/check-acceptance?userId=${customerId}`;

    return this.http.get<any>(url).pipe(
      map(response => {
        return {
          accepted: response.accepted,
        };
      }),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(_message => ({
            ...failStatus,
            message: _message,
          })),
        );
      }),
    );
  }

  confirm(body: { termId: string; accept: boolean }): Observable<GenericRequest<{ accepted: boolean }> | null> {
    if (!body.accept) {
      return of(null);
    }

    const customerId = this.getCustomerId();
    if (!customerId) {
      return this.translateService.get('generic.requestError').pipe(
        map(_message => ({
          ...failStatus,
          message: 'User ID not found',
        })),
      );
    }

    const url = `${environment.apiService}/terms/accept`;

    return this.http.post<{ accepted: boolean }>(url, { termId: body.termId, userId: customerId }).pipe(
      map(response => {
        // Salvar no localStorage após aceitação bem-sucedida
        localStorage.setItem(TERMS_ACCEPTED_KEY, 'true');

        return {
          ...successStatus,
          code: response.accepted,
        };
      }),
      catchError(error => {
        return this.translateService.get('generic.requestError').pipe(
          map(_message => ({
            ...failStatus,
            message: _message,
            code: error.status,
          })),
        );
      }),
    );
  }

  getContent(): Observable<GenericRequest<TermsModalModel>> {
    const url = `${environment.apiService}/terms/current`;

    return this.http.get<TermsModalModel>(url).pipe(
      map(terms => {
        return { data: terms, ...successStatus };
      }),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(_message => ({
            ...failStatus,
            message: _message,
          })),
        );
      }),
    );
  }
}
